/**
 * Knowledge-graph types for the sandbox.
 *
 * The graph captures every relationship the IDE knows about between
 * source files, exported symbols, design specs, and tests. Edges are
 * recomputed every time a node's content changes; cascading lookups
 * stay cheap because the graph is in-memory and the file count is
 * sandbox-scale (low hundreds, not thousands).
 *
 * Naming: `nodeId` is the stable identity used as Map key. For files
 * and tests it's the path; for symbols it's `path#symbolName`; for
 * specs it's the DesignSpec.id ('spec_xxxxxxxx').
 */

/** What kind of thing a node represents. */
export type KGNodeKind = 'file' | 'test' | 'symbol' | 'spec' | 'runbook' | 'asset' | 'api';

/** Which runtime side a $api member lives on. A client/server $api is
 *  {Client|Server}ApiRegistry & SharedApiRegistry, so 'shared' satisfies either. */
export type ApiSide = 'client' | 'server' | 'shared';

/**
 * What relationship an edge encodes. Directed: source → target.
 *
 *   file-imports-file       — source file has `import … from 'target'` (resolved to a project path)
 *   file-exports-symbol     — source file exports the target symbol
 *   spec-describes-symbol   — DesignSpec's `relatedCodeSymbols` lists this symbol
 *   spec-references-spec    — DesignSpec's `relatedSpecIds` lists this other spec
 *   test-covers-file        — a *.spec.ts file imports a non-test file under the project
 *   runbook-addresses-symbol — a Runbook's `relatedCodeSymbols` lists this symbol
 *   runbook-references-spec  — a Runbook's `relatedSpecIds` lists this spec
 *   asset-part-of-group     — a member asset belongs to a composite group node
 *   spec-describes-asset    — a design/asset spec describes this asset
 */
export type KGEdgeKind =
  | 'file-imports-file'
  | 'file-exports-symbol'
  | 'spec-describes-symbol'
  | 'spec-references-spec'
  | 'test-covers-file'
  | 'runbook-addresses-symbol'
  | 'runbook-references-spec'
  | 'file-defines-api'
  | 'file-uses-api'
  | 'file-uses-asset'
  | 'asset-part-of-group'
  | 'spec-describes-asset';

export interface KGNode {
  id: string;
  kind: KGNodeKind;
  /** Display label. For files: the path. For symbols: just the symbol name. For specs: the title. */
  label: string;
  /** Original file path, when applicable (helps the UI link out to the editor). */
  filePath?: string;
  /** Symbol name (for `symbol` nodes). */
  symbolName?: string;
  /** Spec id (for `spec` nodes). */
  specId?: string;
  /** Runbook id (for `runbook` nodes). */
  runbookId?: string;
  /** Asset subtype for `asset` nodes — e.g. 'image' | 'audio' | 'data/json'. */
  assetSubtype?: string;
  /** Content hash for `asset` nodes — drives replacement (same path, new bytes) detection. */
  assetHash?: string;
  /** Composite-group name for `asset` member nodes — e.g. 'player-walk'. */
  assetGroup?: string;
  /** Composite role for `asset` member nodes — drives group-subtype inference. */
  assetRole?: KGAssetCompositeRole;
  /** Spec id that describes this `asset` node — rebuilds spec-describes-asset on spec re-index. */
  assetSpecId?: string;
  /** For `api` nodes: the sides this member is defined on (snapshot-computed). */
  apiSides?: ApiSide[];
  /** For `api` nodes: sides provided by the framework (not a user system), e.g. client canvas/ctx. */
  providedSides?: ApiSide[];
  /** ISO timestamp of the last time this node was (re-)indexed. */
  lastIndexedAt: string;
}

export interface KGEdge {
  /** Deterministic key — `${kind}|${source}|${target}` — so dedup is automatic. */
  key: string;
  kind: KGEdgeKind;
  source: string;
  target: string;
  /**
   * 'resolved' = both endpoints exist in the graph
   * 'dangling' = target doesn't exist (broken reference, stale binding, etc.)
   */
  status: 'resolved' | 'dangling';
  /** For api edges: the side this define/use is on (defineSide / useSide). */
  apiSide?: ApiSide;
  /** When the edge was last (re-)computed. */
  lastUpdatedAt: string;
}

/** Read-only snapshot the UI consumes. */
export interface KGSnapshot {
  nodes: KGNode[];
  edges: KGEdge[];
  /** Monotonic version — increments on every batched update so React deps can compare cheaply. */
  version: number;
  /** When the last update completed. */
  lastUpdatedAt: string;
  /**
   * Stats summarising the graph at a glance — used by the panel header.
   */
  stats: {
    files: number;
    tests: number;
    symbols: number;
    specs: number;
    assets: number;
    apis: number;
    runbooks: number;
    edges: number;
    danglingEdges: number;
  };
}

export const KG_EDGE_LABEL: Record<KGEdgeKind, string> = {
  'file-imports-file':        'imports',
  'file-exports-symbol':      'exports',
  'spec-describes-symbol':    'describes',
  'spec-references-spec':     'references',
  'test-covers-file':         'covers',
  'runbook-addresses-symbol': 'addresses',
  'runbook-references-spec':  'references',
  'file-defines-api':         'defines',
  'file-uses-api':            'uses',
  'file-uses-asset':          'uses',
  'asset-part-of-group':      'part of',
  'spec-describes-asset':     'describes',
};

/**
 * Minimum spec shape the KG needs. The sandbox UI's full `DesignSpec`
 * interface is a structural superset, so it can be passed directly
 * to `onSpecChanged` without conversion.
 */
export interface KGSpecInput {
  id: string;
  title: string;
  relatedCodeSymbols?: Array<{ path: string; symbol?: string }>;
  relatedSpecIds?: string[];
}

/**
 * Minimum runbook shape the KG needs. sandbox-ai-v2's full `Runbook` is a
 * structural superset, so it can be passed directly to `onRunbookChanged`.
 */
export interface KGRunbookInput {
  id: string;
  title: string;
  relatedCodeSymbols?: Array<{ path: string; symbol?: string }>;
  relatedSpecIds?: string[];
}

/**
 * Role a composite member plays within its group. Mirrors AssetSpecPayload
 * composite parts: sprite | animation (sheet + frames) | tileset
 * (tileset-image + tilemap) | sfx | music | data.
 */
export type KGAssetCompositeRole =
  | 'sheet'
  | 'frames'
  | 'tileset-image'
  | 'tilemap'
  | 'sfx'
  | 'music'
  | 'sprite'
  | 'data';

/** Minimum asset shape the KG needs. Fed from the sandbox assetStore. */
export interface KGAssetInput {
  /** /assets-relative path, e.g. 'images/player.png'. */
  path: string;
  /** Display subtype, e.g. 'image' | 'audio' | 'data/json'. */
  subtype: string;
  /** Content hash — drives replacement detection (same path, new bytes = in-place swap). */
  hash?: string;
  /**
   * Composite-group membership: which group this asset belongs to and the
   * role it plays. e.g. an animation's image sheet is
   * { group: 'player-walk', role: 'sheet' } and its atlas json is
   * { group: 'player-walk', role: 'frames' }.
   */
  composite?: { group: string; role: KGAssetCompositeRole };
  /** Design/asset spec linkage (pipeline-generated assets carry this). */
  specId?: string;
}
